import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { StatusShare } from "@/components/dm/StatusShare";
import { MessageCircle, Share2, ShieldAlert, MapPin, CalendarDays, X, ChevronLeft, Pencil, Store } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { api, pushRecent, imgUrl } from "@/lib/api";
import { useContact, useShare } from "@/lib/contact";
import { SaveHeart, PostCard } from "@/components/dm/PostCard";
import { ReportButton } from "@/components/dm/ReportDialog";
import { Questions } from "@/components/dm/Questions";
import { Avatar, Photo, VouchPill, VerifiedCheck, TypeBadge, ExampleBadge, BusinessBadge, FeaturedBadge, BuilderBadge, StatusBadge, usePriceText, useDate, CategoryIcon } from "@/components/dm/ui";

export const Gallery = ({ photos, icon, title }) => {
  const { t } = useApp();
  const [idx, setIdx] = useState(0);
  const [full, setFull] = useState(false);
  const ref = useRef(null);
  const onScroll = () => { const el = ref.current; if (el) setIdx(Math.round(el.scrollLeft / el.clientWidth)); };
  if (!photos?.length) return <Photo src={null} icon={icon} className="aspect-[4/3] w-full sm:rounded-[24px]" testid="gallery" />;
  return (
    <div className="relative" data-testid="gallery">
      <div ref={ref} onScroll={onScroll} className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar sm:rounded-[24px]">
        {photos.map((p, i) => (
          <button key={i} data-testid={`gallery-photo-${i}`} onClick={() => setFull(true)} className="snap-center shrink-0 w-full">
            <Photo src={p} alt={`${title} ${i + 1}`} icon={icon} className="aspect-[4/3] w-full" />
          </button>
        ))}
      </div>
      {photos.length > 1 && (<>
        <span data-testid="photo-counter" className="absolute top-3 right-3 rounded-full bg-[#291E18]/75 text-white text-xs font-bold px-3 py-1">{t("photo_counter", { a: idx + 1, b: photos.length })}</span>
        <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5">{photos.map((_, i) => <span key={i} className={`h-2 rounded-full transition-all duration-200 ${i === idx ? "w-5 bg-[#F59E0B]" : "w-2 bg-white/80"}`} />)}</div>
      </>)}
      <Dialog open={full} onOpenChange={setFull}>
        <DialogContent data-testid="fullscreen-viewer" className="max-w-none w-screen h-screen p-0 bg-black border-0 rounded-none [&>button]:hidden">
          <DialogTitle className="sr-only">{title}</DialogTitle><DialogDescription className="sr-only">Photos</DialogDescription>
          <button data-testid="viewer-close" onClick={() => setFull(false)} aria-label="Close" className="absolute top-4 right-4 z-10 w-12 h-12 rounded-full bg-white/15 text-white flex items-center justify-center"><X /></button>
          <div className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar h-full">
            {photos.map((p, i) => <div key={i} className="snap-center shrink-0 w-full h-full flex items-center justify-center"><img src={imgUrl(p)} alt="" className="max-w-full max-h-full object-contain" /></div>)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SellerBlock = ({ poster }) => {
  const { t } = useApp();
  const date = useDate();
  return (
    <Link to={`/u/${poster.id}`} data-testid="seller-block" className="press flex items-center gap-4 p-4 rounded-[20px] bg-[#FFFDF9] border border-[#E8E2D9] card-shadow">
      <Avatar user={poster} size={56} />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-1.5 flex-wrap"><span className="font-extrabold text-lg truncate">{poster.name}</span>{poster.phone_verified && <VerifiedCheck label={t("verified_phone")} />}{poster.is_demo && <ExampleBadge />}</div>
        <div className="flex flex-wrap gap-1.5 items-center">
          {poster.phone_verified && <span className="text-xs font-semibold text-[#14532D]">{t("verified_phone")}</span>}
          <VouchPill n={poster.vouch_count} testid="seller-vouch-pill" /><BusinessBadge business={poster.business} /><BuilderBadge user={poster} />
        </div>
        <div className="text-xs text-[#6B5E55]">{t("member_since", { date: date(poster.member_since, { month: "long", year: "numeric" }) })}</div>
      </div>
    </Link>
  );
};

const SafetyLines = ({ p }) => {
  const { t } = useApp();
  const extra = ["housing", "jobs"].includes(p.category_id) ? t("safety_housing") : p.category_id === "shipping-barrels" ? t("safety_shipping") : null;
  return (
    <div className="space-y-2">
      {extra && <div data-testid="category-safety" className="flex gap-3 p-4 rounded-2xl bg-[#FFF4EA] border border-[#C2410C]/30 text-sm font-semibold text-[#7C2D12]"><ShieldAlert className="w-5 h-5 shrink-0" aria-hidden="true" />{extra}</div>}
      <div data-testid="safety-note" className="flex gap-3 p-4 rounded-2xl bg-[#ECF3EF] text-sm text-[#133E2F]"><ShieldAlert className="w-5 h-5 shrink-0" aria-hidden="true" />{t("safety_note")}</div>
    </div>
  );
};

export const StickyBar = ({ p, isOwner }) => {
  const { t } = useApp();
  const contact = useContact();
  const share = useShare();
  const navigate = useNavigate();
  const closed = ["sold", "found"].includes(p.status);
  return (
    <div className="fixed bottom-[calc(72px+env(safe-area-inset-bottom))] inset-x-0 z-30 px-3" data-testid="sticky-contact-bar">
      <div className="max-w-3xl mx-auto rounded-full bg-[#FFFDF9]/95 backdrop-blur-xl border border-[#E8E2D9] shadow-[0_10px_30px_rgba(41,30,24,0.18)] p-1.5 flex items-center gap-1.5">
        <SaveHeart id={p.id} />
        <button data-testid="share-button" aria-label={t("share")} onClick={() => share(p)} className="press w-11 h-11 rounded-full bg-[#F3EDE4] flex items-center justify-center"><Share2 className="w-5 h-5" /></button>
        {isOwner ? (
          <button data-testid="owner-edit-button" onClick={() => navigate(`/edit/${p.id}`)} className="press flex-1 min-h-[52px] rounded-full bg-[#1E4D3B] text-white font-extrabold inline-flex items-center justify-center gap-2"><Pencil className="w-5 h-5" />{t("edit")}</button>
        ) : (
          <button data-testid="whatsapp-contact-button" disabled={closed} onClick={() => contact(p)}
            className="press wa-pulse-once flex-1 min-h-[52px] rounded-full bg-[#128C7E] hover:bg-[#0f7a6d] text-white font-extrabold text-base inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <MessageCircle className="w-5 h-5" aria-hidden="true" />{t("contact_whatsapp")}
          </button>
        )}
      </div>
    </div>
  );
};

export default function PostDetail() {
  const { id } = useParams();
  const { t, L, cat, hubOf, areaOf, user } = useApp();
  const price = usePriceText();
  const date = useDate();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setP(null); window.scrollTo(0, 0);
    api.get(`/posts/${id}`).then((r) => { setP(r.data); pushRecent(r.data); }).catch(() => setErr(true));
  }, [id]);
  if (err) return <div className="max-w-3xl mx-auto p-8 text-center font-bold" data-testid="post-not-found">404</div>;
  if (!p) return <div className="max-w-3xl mx-auto sm:p-4"><div className="skeleton aspect-[4/3] sm:rounded-[24px]" /><div className="p-4 space-y-3"><div className="skeleton h-8 w-2/3 rounded-full" /><div className="skeleton h-5 w-1/3 rounded-full" /></div></div>;
  const c = cat(p.category_id);
  const isReq = p.type === "request";
  const isOwner = user?.id === p.user_id;
  const area = areaOf(p.area_id);
  return (
    <div className="max-w-3xl mx-auto sm:px-4 sm:pt-4 pb-32" data-testid="post-detail">
      <div className="relative">
        <button data-testid="back-button" onClick={() => navigate(-1)} aria-label={t("back")} className="press absolute top-3 left-3 z-10 w-11 h-11 rounded-full bg-[#FFFDF9]/90 backdrop-blur flex items-center justify-center shadow"><ChevronLeft className="w-5 h-5" /></button>
        {isReq && !p.photos?.length ? (
          <div className="bg-[#FFF4EA] border-b-4 border-[#C2410C] sm:rounded-[24px] px-6 pt-16 pb-8 relative overflow-hidden">
            <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full ankara-dots opacity-50" aria-hidden="true" />
            <CategoryIcon icon={c?.icon} className="w-10 h-10 text-[#C2410C]" />
          </div>
        ) : <Gallery photos={p.photos} icon={c?.icon} title={p.title} />}
      </div>
      <div className="px-4 pt-5 space-y-5">
        <div className="flex flex-wrap gap-1.5 items-center"><TypeBadge type={p.type} /><StatusBadge status={p.is_expired ? "expired" : p.status} /><FeaturedBadge post={p} />{p.is_demo && <ExampleBadge />}<BusinessBadge business={p.poster?.business} />
          <span className="inline-flex items-center gap-1 rounded-full bg-[#F3EDE4] px-2.5 py-0.5 text-xs font-semibold"><CategoryIcon icon={c?.icon} className="w-3 h-3" />{L(c)}</span></div>
        <h1 data-testid="post-title" className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">{p.title}</h1>
        {price(p) && <div data-testid="post-price" className="text-2xl font-extrabold text-[#92400E]">{price(p)}</div>}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#6B5E55]">
          <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" aria-hidden="true" />{L(hubOf(p.hub_id))}{area ? ` · ${L(area)}` : ""}</span>
          <span className="inline-flex items-center gap-1"><CalendarDays className="w-4 h-4" aria-hidden="true" />{t("posted")} {date(p.created_at, { day: "numeric", month: "long" })}</span>
        </div>
        {p.description && <p data-testid="post-description" className="text-base leading-relaxed whitespace-pre-line">{p.description}</p>}
        {!p.is_demo && <StatusShare p={p} />}
        {isReq && !isOwner && <p className="text-lg font-extrabold text-[#9A3412]">{t("can_you_help")}</p>}
        <SafetyLines p={p} />
        {p.poster && <SellerBlock poster={p.poster} />}
        {p.in_shop && p.poster?.business && (
          <Link to={p.poster.business.slug ? `/${p.poster.business.slug}` : `/u/${p.poster.id}#shop`} data-testid="shop-see-all" className="press flex items-center justify-center gap-2 min-h-[48px] rounded-full bg-[#FEF3C7] text-[#92400E] font-extrabold">
            <Store className="w-5 h-5" />{t("shop_see_all")}
          </Link>
        )}
        {p.other_posts?.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-base md:text-lg font-extrabold">{t("other_posts")}</h2>
            <div className="flex gap-4 overflow-x-auto no-scrollbar snap-x -mx-4 px-4 pb-2" data-testid="other-posts">
              {p.other_posts.map((o) => <div key={o.id} className="snap-start shrink-0 w-64"><PostCard p={o} /></div>)}
            </div>
          </section>
        )}
        <Questions p={p} isOwner={isOwner} />
        <div className="flex gap-2"><ReportButton itemType="post" itemId={p.id} /></div>
      </div>
      <StickyBar p={p} isOwner={isOwner} />
    </div>
  );
}
